/**
 * GameStateSnapshot - Immutable game state for minimax search tree
 *
 * Creates lightweight, immutable snapshots of the game state that can be
 * efficiently cloned and modified during tree search.
 */

import { GameState, Position, FactionType, CityLevel } from '../../../types';
import { UnitManager } from '../../../UnitManager';
import { GameMap } from '../../../Map';
import { UNIT_DEFINITIONS } from '../../../unitDefinitions';
import { getCityMaxUnits } from '../../../cityUtils';
import {
  GameStateSnapshot,
  UnitSnapshot,
  CitySnapshot,
  positionToKey,
  GeneratedMove,
  MoveType
} from '../V3Types';

/**
 * Creates an immutable snapshot of the current game state
 */
export function createSnapshot(
  gameState: GameState,
  unitManager: UnitManager,
  map: GameMap,
  aiFaction: FactionType,
  enemyFaction: FactionType,
  campaign?: 'normandy' | 'tunisia' | 'stalingrad' | 'okinawa' | 'campobasso' | 'tutorial' | 'france_1940' | 'albania_1940'
): GameStateSnapshot {
  // Convert units to snapshots
  const units: UnitSnapshot[] = [];
  const occupiedPositions = new Set<string>();

  for (const unit of unitManager.getAllUnits()) {
    const definition = UNIT_DEFINITIONS[unit.type];

    units.push({
      id: unit.id,
      type: unit.type,
      faction: unit.faction,
      position: { ...unit.position },
      health: unit.stats.health,
      maxHealth: unit.stats.maxHealth,
      movementRemaining: unit.movementRemaining,
      hasAttacked: unit.hasAttacked,
      attack: unit.stats.attack,
      defense: unit.stats.defense,
      range: unit.stats.range,
      movement: unit.stats.movement,
      category: unit.category,
      isVeteran: unit.isVeteran || false,
      cost: definition.cost,
      isCarryingUnit: !!(unit.carryingUnit || (unit.carryingUnits && unit.carryingUnits.length > 0)),
      towingUnit: !!unit.towingUnit
    });

    occupiedPositions.add(positionToKey(unit.position));
  }

  // Convert cities to snapshots
  const cities: CitySnapshot[] = [];
  for (const city of map.getCities().values()) {
    const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];

    // Count units produced (alive units from this city)
    const unitsProduced = city.unitsProduced?.length || 0;
    // Count units in training queue
    const unitsInTraining = city.trainingQueue?.length || 0;

    cities.push({
      name: city.name,
      faction: city.faction,
      level: city.level,
      centerPosition: { ...centerTile },
      tiles: city.tiles.map(t => ({ ...t })),  // Copy all tiles
      isBeingCaptured: !!city.captureProgress,
      capturingFaction: city.captureProgress?.faction || null,
      unitsProduced,
      unitsInTraining,
      maxUnits: getCityMaxUnits(city.level),
      isUpgrading: !!city.upgradeInProgress
    });
  }

  // Copy resources
  const resources = new Map<FactionType, number>();
  for (const [faction, amount] of gameState.resources) {
    resources.set(faction as FactionType, amount);
  }

  return {
    turn: gameState.turn,
    currentFaction: aiFaction,
    enemyFaction: enemyFaction,
    units,
    cities,
    resources,
    occupiedPositions,
    mapWidth: map.getWidth(),
    mapHeight: map.getHeight(),
    campaign
  };
}

/**
 * Clone a snapshot with modifications (for search tree)
 */
export function cloneSnapshot(snapshot: GameStateSnapshot): GameStateSnapshot {
  return {
    turn: snapshot.turn,
    currentFaction: snapshot.currentFaction,
    enemyFaction: snapshot.enemyFaction,
    units: [...snapshot.units],
    cities: [...snapshot.cities],
    resources: new Map(snapshot.resources),
    occupiedPositions: new Set(snapshot.occupiedPositions),
    mapWidth: snapshot.mapWidth,
    mapHeight: snapshot.mapHeight,
    campaign: snapshot.campaign
  };
}

/**
 * Apply a move to a snapshot, returning a new snapshot
 */
export function applyMove(snapshot: GameStateSnapshot, move: GeneratedMove): GameStateSnapshot {
  const newUnits = [...snapshot.units];
  const newOccupied = new Set(snapshot.occupiedPositions);
  const newResources = new Map(snapshot.resources);
  const newCities = [...snapshot.cities];

  const unitIndex = newUnits.findIndex(u => u.id === move.unitId);

  switch (move.type) {
    case MoveType.MOVE: {
      if (unitIndex >= 0 && move.to) {
        const unit = newUnits[unitIndex];
        // Remove old position
        newOccupied.delete(positionToKey(unit.position));
        // Update unit
        newUnits[unitIndex] = {
          ...unit,
          position: move.to,
          movementRemaining: 0 // Simplified - full move uses all movement
        };
        // Add new position
        newOccupied.add(positionToKey(move.to));
      }
      break;
    }

    case MoveType.ATTACK: {
      if (unitIndex >= 0 && move.targetId) {
        const attacker = newUnits[unitIndex];
        const targetIndex = newUnits.findIndex(u => u.id === move.targetId);

        if (targetIndex >= 0) {
          const target = newUnits[targetIndex];

          // Calculate expected damage (simplified)
          const damage = calculateExpectedDamage(attacker, target);
          const newHealth = target.health - damage;

          if (newHealth <= 0) {
            // Target destroyed
            newOccupied.delete(positionToKey(target.position));
            newUnits.splice(targetIndex, 1);
          } else {
            // Target damaged
            newUnits[targetIndex] = {
              ...target,
              health: newHealth
            };
          }

          // Mark attacker as having attacked
          const updatedIndex = newUnits.findIndex(u => u.id === move.unitId);
          if (updatedIndex >= 0) {
            newUnits[updatedIndex] = {
              ...newUnits[updatedIndex],
              hasAttacked: true
            };
          }
        }
      }
      break;
    }

    case MoveType.MOVE_AND_ATTACK: {
      if (unitIndex >= 0 && move.to && move.targetId) {
        const unit = newUnits[unitIndex];

        // Move first
        newOccupied.delete(positionToKey(unit.position));
        newUnits[unitIndex] = {
          ...unit,
          position: move.to,
          movementRemaining: 0
        };
        newOccupied.add(positionToKey(move.to));

        // Then attack
        const targetIndex = newUnits.findIndex(u => u.id === move.targetId);
        if (targetIndex >= 0) {
          const target = newUnits[targetIndex];
          const damage = calculateExpectedDamage(newUnits[unitIndex], target);
          const newHealth = target.health - damage;

          if (newHealth <= 0) {
            newOccupied.delete(positionToKey(target.position));
            newUnits.splice(targetIndex, 1);
          } else {
            newUnits[targetIndex] = {
              ...target,
              health: newHealth
            };
          }

          // Mark as attacked
          const updatedIndex = newUnits.findIndex(u => u.id === move.unitId);
          if (updatedIndex >= 0) {
            newUnits[updatedIndex] = {
              ...newUnits[updatedIndex],
              hasAttacked: true
            };
          }
        }
      }
      break;
    }

    case MoveType.HUNKER: {
      if (unitIndex >= 0) {
        // Hunker provides defense bonus but uses turn
        newUnits[unitIndex] = {
          ...newUnits[unitIndex],
          movementRemaining: 0,
          hasAttacked: true
        };
      }
      break;
    }

    case MoveType.BUILD: {
      // Deduct resources (unit will appear next turn, not in this snapshot)
      if (move.unitType) {
        const def = UNIT_DEFINITIONS[move.unitType];
        const currentResources = newResources.get(snapshot.currentFaction) || 0;
        newResources.set(snapshot.currentFaction, currentResources - def.cost);
      }
      break;
    }

    case MoveType.UPGRADE_CITY: {
      // Deduct resources for upgrade
      if (move.cityName && move.upgradeLevel) {
        const cityIndex = newCities.findIndex(c => c.name === move.cityName);
        if (cityIndex >= 0) {
          // Upgrade costs vary by level
          const upgradeCost = move.upgradeLevel === CityLevel.FACTORY ? 40 : 60;
          const currentResources = newResources.get(snapshot.currentFaction) || 0;
          newResources.set(snapshot.currentFaction, currentResources - upgradeCost);
        }
      }
      break;
    }

    case MoveType.PASS:
    default:
      // No changes
      break;
  }

  return {
    turn: snapshot.turn,
    currentFaction: snapshot.currentFaction,
    enemyFaction: snapshot.enemyFaction,
    units: newUnits,
    cities: newCities,
    resources: newResources,
    occupiedPositions: newOccupied,
    mapWidth: snapshot.mapWidth,
    mapHeight: snapshot.mapHeight,
    campaign: snapshot.campaign
  };
}

/**
 * Calculate expected damage from attacker to defender
 * Simplified combat model for search
 */
export function calculateExpectedDamage(attacker: UnitSnapshot, defender: UnitSnapshot): number {
  // Base damage calculation
  const attackPower = attacker.attack * (attacker.isVeteran ? 1.25 : 1);
  const defensePower = defender.defense * (defender.isVeteran ? 1.25 : 1);

  // Damage formula (simplified from actual game)
  const baseDamage = Math.max(1, attackPower - defensePower / 2);

  // Health factor - damaged units deal less damage
  const healthFactor = attacker.health / attacker.maxHealth;

  return Math.floor(baseDamage * healthFactor);
}

/**
 * Get units for a specific faction from snapshot
 */
export function getUnitsForFaction(snapshot: GameStateSnapshot, faction: FactionType): UnitSnapshot[] {
  return snapshot.units.filter(u => u.faction === faction);
}

/**
 * Get active units (can still move or attack) for a faction
 */
export function getActiveUnits(snapshot: GameStateSnapshot, faction: FactionType): UnitSnapshot[] {
  return snapshot.units.filter(u =>
    u.faction === faction &&
    (u.movementRemaining > 0 || !u.hasAttacked)
  );
}

/**
 * Get cities controlled by a faction
 */
export function getCitiesForFaction(snapshot: GameStateSnapshot, faction: FactionType): CitySnapshot[] {
  return snapshot.cities.filter(c => c.faction === faction);
}

/**
 * Check if a position is within map bounds
 */
export function isValidPosition(snapshot: GameStateSnapshot, pos: Position): boolean {
  return pos.x >= 0 && pos.x < snapshot.mapWidth &&
         pos.y >= 0 && pos.y < snapshot.mapHeight;
}

/**
 * Check if a position is occupied
 */
export function isOccupied(snapshot: GameStateSnapshot, pos: Position): boolean {
  return snapshot.occupiedPositions.has(positionToKey(pos));
}

/**
 * Get unit at a position
 */
export function getUnitAt(snapshot: GameStateSnapshot, pos: Position): UnitSnapshot | null {
  return snapshot.units.find(u =>
    u.position.x === pos.x && u.position.y === pos.y
  ) || null;
}

/**
 * Switch perspective to enemy faction (for minimax)
 */
export function switchPerspective(snapshot: GameStateSnapshot): GameStateSnapshot {
  return {
    ...snapshot,
    currentFaction: snapshot.enemyFaction,
    enemyFaction: snapshot.currentFaction
  };
}
